import { useMemo } from 'react';
import { IncludesTrialBackfillDetail } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { CallIntelligenceWetags } from '@wetags/call-intelligence';
import { CallIntelligenceV2, CallIntelTypes } from '@frontend/api-call-intel';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useCallIntelSubscription } from '@frontend/call-intel-core';
import { useAppScopeStore } from '@frontend/scope';
import { useLocation } from '@frontend/weave-router';
import { CALL_INTEL_BASE_URL } from '../constants';
import { deriveBackfillUIFlags } from '../utils';

interface UseCallIntelTrialProps {
  enabled?: boolean;
  locationIds?: string[];
}

export const useCallIntelTrial = ({ enabled, locationIds }: UseCallIntelTrialProps = {}) => {
  const [isWetagEnabled] = CallIntelligenceWetags.useWetag('call-intel-trial-backfill');
  const isTrialBackfillEnabled = enabled ?? isWetagEnabled;

  const { selectedLocationIds } = useAppScopeStore();
  const location = useLocation();

  const effectiveLocationIds = locationIds?.length ? locationIds : selectedLocationIds;

  const { trialActiveLocationIds, isLoading: isSubscriptionLoading } = useCallIntelSubscription({
    locationIds: isTrialBackfillEnabled ? effectiveLocationIds : [],
  });

  const hasTrialLocations = trialActiveLocationIds.length > 0;
  const allLocationsAreTrial =
    effectiveLocationIds.length > 0 && trialActiveLocationIds.length === effectiveLocationIds.length;
  const hasTrialAndSubscribedLocations = hasTrialLocations && !allLocationsAreTrial;

  const isOnCallIntelligenceRoute = location.pathname.startsWith(CALL_INTEL_BASE_URL);
  const hasTrialAccess = trialActiveLocationIds.some((locId) => hasSchemaACL(locId, Permission.CALL_INTELLIGENCE_READ));

  const { data: backfillStatus, isLoading: isBackfillLoading } =
    CallIntelligenceV2.Queries.useGetTrialBackfillStatusQuery({
      request: {
        includes: [IncludesTrialBackfillDetail.INCLUDES_TRIAL_BACKFILL_LOCATION_STATUSES],
        locationIds: effectiveLocationIds,
      },
      options: {
        enabled: isTrialBackfillEnabled && hasTrialLocations && effectiveLocationIds.length > 0,
      },
    });

  const backfillFlags = useMemo(() => deriveBackfillUIFlags(backfillStatus), [backfillStatus]);

  const trialProcessingLocationIds = useMemo(() => {
    if (!backfillStatus?.locationStatuses) return [];

    return backfillStatus.locationStatuses
      .filter((loc) => {
        const state = loc.state;
        return (
          state === CallIntelTypes.TrialBackfillStateEnum.TRIAL_BACKFILL_STATE_PENDING ||
          state === CallIntelTypes.TrialBackfillStateEnum.TRIAL_BACKFILL_STATE_BATCH1_IN_PROGRESS ||
          state === CallIntelTypes.TrialBackfillStateEnum.TRIAL_BACKFILL_STATE_WAITING_FOR_LIVE_CALLS
        );
      })
      .map((loc) => loc.locationId);
  }, [backfillStatus]);

  const isLoading = isSubscriptionLoading || isBackfillLoading;

  return {
    ...backfillFlags,
    allLocationsAreTrial,
    backfillStatus,
    hasTrialAccess,
    hasTrialAndSubscribedLocations,
    hasTrialLocations,
    isBackfillLoading,
    isLoading,
    isOnCallIntelligenceRoute,
    isSubscriptionLoading,
    selectedLocationIds: effectiveLocationIds,
    trialProcessingLocationIds,
  };
};
